/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	Self,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, NgControl, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { take, tap, takeUntil, map, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { ICustomFieldTicket } from 'src/app/app-data/models/CustomFieldTicketValue';
import {
	CustomFieldListType,
	CustomFieldsApi,
	CustomFieldType,
} from 'src/app/app-data/state/CustomFieldsApi';
import { DefaultSplitDataRenderType, TicketType } from '@touchplan/common';

import { CustomFieldService } from 'src/app/modules/custom-field/services/custom-field.services';

import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { MultiWrapperComponent } from 'src/app/modules/ticket-edit/components/multi-wrapper/multi-wrapper.component';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';

@Component({
	selector: 'custom-fields-list',
	templateUrl: './custom-fields-list.component.html',
	styleUrls: ['./custom-fields-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('CustomFieldsListComponent')],
})
export class CustomFieldsListComponent implements OnInit, OnDestroy {
	@Input() public customFieldsApi: CustomFieldsApi;

	/**
	 * This is the FormGroup used by this <custom-fields-list>.  It is created by the
	 * UI component using this list, and passed in as an @Input.  Because the list
	 * subcomponents are only known at runtime, this class adds FormControls to this
	 * FormGroup at runtime in the "createListElementFormControls" method, once the
	 * "CustomFieldDefinition"s have been determined.
	 *
	 * The name given to each FormControl in this FormGroup is - for convenience - the
	 * ID of the corresponding CustomFieldDefinition.  That makes it easy to then go
	 * from a value to store into a FormControl (when values are read from Firebase);
	 * the definition ID corresponding to the value is known from Firebase, so it is
	 * used to get the correct FormControl from this FormGroup; see "initializeListElements".
	 */
	@Input() public customFieldsListFormGroup: UntypedFormGroup;

	@Input() public ticketEditCombinedData: EditTicketsCombined;

	@Input() public editedTicketId: string;

	@Input() public saveCustomFieldsRequested$: Observable<void>;

	@Input() public cancelCustomFieldsRequested$: Observable<void>;

	@Input() public isFloatingTickets: boolean;

	@Input() public allowList$: Observable<string[]>;

	@Input() private combinedEdit$?: BehaviorSubject<EditTicketsCombined>;

	@Input() public planId: string;

	public orderedCustomFieldDef$: Observable<CustomFieldDefinition[]>;
	public addExtraScrollWindowSpace$: Observable<boolean>;

	// Constants for use in the template:
	public TypeTextLC = CustomFieldType.Text.toLowerCase();
	public TypeNumberLC = CustomFieldType.Number.toLowerCase();
	public TypeDropDownListLC = CustomFieldType.DropDownList.toLowerCase();
	public TypeLinkLC = CustomFieldType.Link.toLowerCase();

	private currentSubscriptions: Subscription[] = [];
	private shutdownSubject: Subject<boolean> = new Subject();

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		private customFieldService: CustomFieldService,
		private logger: DefaultLoggerService
	) {}

	public ngOnInit(): void {
		this.setup();
		this.listenForRequests();
	}

	private setup() {
		// Initialize the "this.orderedCustomFieldDef$" used by <custom-fields-list> subcomponents,
		// but make sure that the FormControl elements in the "customFieldsFormArray" FormArray are
		// created/updated before the <custom-field-list> subcomponents get the definitions:
		this.orderedCustomFieldDef$ = this.combinedEdit$?.pipe(
			// This filters out data we get when tickets have been de-selected:
			// filter(data => data.originalList.size > 0 || !data.originalList),
			// Get the type of the ticket being edited (which doesn't change for a given ticket edit)
			// and the applicable "ticket role" (which can change during an edit):

			// TODO: While on mobile, we get data of GroupedTicket here. So the further logic in some cases is incorrect.
			map((data: any) => {
				let type = this.getTicketType(data);
				type = type ? type : data.view?.type ? data.view.type : TicketType.Task;
				return {
					ticketType: type,
					roleId: data.data
						? this.customFieldService.getRoleId(data.data)
						: this.customFieldService.getRoleId(data.view),
				};
			}),
			distinctUntilChanged(
				(pair1, pair2) => pair1.ticketType === pair2.ticketType && pair1.roleId === pair2.roleId
			),
			switchMap(
				(pair: { ticketType: TicketType; roleId: string }) =>
					this.customFieldsApi
						.getFilteredDefinitionsForTicket$(pair.ticketType, pair.roleId)
						.pipe(distinctUntilChanged()) // This prevents updates to the definitions from leading to edit UI updates
			),
			tap((defs) => {
				const listType: CustomFieldListType = this.customFieldService.getTicketValueListType(
					this.isFloatingTickets
				);
				this.customFieldsApi.addTicketValue(this.editedTicketId, listType);
				this.customFieldsApi.changeTicketValueView(this.editedTicketId, listType);
				const roleIdsForCustomFieldsToInit = this.updateListElementFormControls(defs);
				this.initializeListElements(roleIdsForCustomFieldsToInit);
				this.listenForListElementChanges(defs);
			}),
			takeUntil(this.shutdownSubject)
		);

		this.addExtraScrollWindowSpace$ = this.orderedCustomFieldDef$.pipe(
			map((defs) => defs.length > 0),
			takeUntil(this.shutdownSubject)
		);
	}

	private getTicketType(data: EditTicketsCombined) {
		if (data.selectedType) {
			return data.isAnyConstraint(data.selectedType)
				? TicketType.Constraint
				: this.customFieldService.getTicketType(data.selectedType);
		}
		return null;
	}

	private updateListElementFormControls(defs: CustomFieldDefinition[]): string[] {
		// First, detach any FormControls for child custom field elements that no longer apply:
		const roleIdsForCustomFieldsToRevert: string[] = [];
		for (const key of Object.keys(this.customFieldsListFormGroup.controls)) {
			const matches = defs.filter((def) => def.$id === key);
			const noLongerVisible = !matches || matches.length !== 1;
			if (noLongerVisible) {
				this.customFieldsListFormGroup.removeControl(key);
				roleIdsForCustomFieldsToRevert.push(key);
			}
		}
		this.customFieldsApi.revertTicketValues(this.editedTicketId, roleIdsForCustomFieldsToRevert);
		// Now create FormControls for any newly-added custom fields:
		const roleIdsForCustomFieldsToInit: string[] = [];
		for (const def of defs) {
			const formControl = this.customFieldsListFormGroup.get(def.$id);
			if (!formControl) {
				const initialText = def.view.type.toLowerCase() === this.TypeDropDownListLC ? undefined : '';
				const fc = new UntypedFormControl({
					value: initialText,
					disabled: this.customFieldsListFormGroup.disabled,
				});
				if (def.view.isRequired) {
					fc.addValidators(Validators.required);
					fc.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				}
				this.customFieldsListFormGroup.addControl(def.$id, fc);
				roleIdsForCustomFieldsToInit.push(def.$id);
			} else {
				if (def.view.isRequired && !formControl.hasValidator(Validators.required)) {
					formControl.addValidators(Validators.required);
					formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				} else if (!def.view.isRequired && formControl.hasValidator(Validators.required)) {
					formControl.removeValidators(Validators.required);
					formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				}
			}
		}
		return roleIdsForCustomFieldsToInit;
	}

	private initializeListElements(roleIdsForCustomFieldsToInit: string[]) {
		const listType: CustomFieldListType = this.customFieldService.getTicketValueListType(
			this.isFloatingTickets
		);
		this.customFieldsApi
			.getCustomFieldTicketValues(listType)
			.data.list$.pipe(take(1), takeUntil(this.shutdownSubject))
			.subscribe((mapObj) => {
				const customFieldTicketData = mapObj.get(this.editedTicketId);
				if (customFieldTicketData) {
					const customFieldTicketDataObj = customFieldTicketData.view;
					for (const customFieldDefKey of Object.keys(customFieldTicketDataObj)) {
						if (roleIdsForCustomFieldsToInit.includes(customFieldDefKey)) {
							const formControl = this.customFieldsListFormGroup.get(customFieldDefKey);
							if (formControl) {
								formControl.setValue(customFieldTicketDataObj[customFieldDefKey].value);
							}
						}
					}
				}
			});
	}

	private listenForListElementChanges(defs: CustomFieldDefinition[]) {
		// First, unsubscribe any previous subscriptions:
		for (const subscription of this.currentSubscriptions) {
			subscription.unsubscribe();
		}
		this.currentSubscriptions = [];

		// Now create subscriptions which listen for value changes in the custom field list's widgets:
		const controls = this.customFieldsListFormGroup.controls;
		for (const subKey of Object.keys(controls)) {
			const defForControl = defs.find((def) => def.$id === subKey);
			const singleCustomFieldsFormControl = this.customFieldsListFormGroup.get(subKey);
			const subscription = singleCustomFieldsFormControl.valueChanges.subscribe((value) => {
				const customFieldTypeLC = defForControl.view.type.toLowerCase();
				let valueToStore: string | number;
				// Note that sending "null" to the CustomFieldsApi causes the Firebase element for the custom field value
				// to be deleted, which is a good thing, since that matches the state of a "not yet set" field, and all of
				// the kinds of custom-field-list components need to handle such not-yet-set cases.  Plus removing null
				// elements ends up using less Firebase storage.
				if (value === undefined || value === null) {
					valueToStore = null;
				} else if (customFieldTypeLC === this.TypeTextLC) {
					valueToStore = this.handleNewTextCustomFieldValue(defForControl, value);
				} else if (customFieldTypeLC === this.TypeNumberLC) {
					valueToStore = this.handleNewNumberCustomFieldValue(defForControl, value);
				} else if (customFieldTypeLC === this.TypeDropDownListLC) {
					valueToStore = this.handleNewDropDownCustomFieldValue(defForControl, value);
				} else if (customFieldTypeLC === this.TypeLinkLC) {
					valueToStore = this.handleNewLinkCustomFieldValue(defForControl, value);
				}
				const payload: ICustomFieldTicket = {};
				payload[subKey] = { value: valueToStore };
				const listType: CustomFieldListType = this.customFieldService.getTicketValueListType(
					this.isFloatingTickets
				);
				this.customFieldsApi.localUpdateTicketValue(this.editedTicketId, payload, listType);
				// this.logger.log('localUpdateTicketValue(..., subKey = ' + subKey + ', value = ' + valueToStore + ')');
			});
			this.currentSubscriptions.push(subscription);
		}
	}

	private handleNewDropDownCustomFieldValue(defForControl: CustomFieldDefinition, value: any): string {
		let idValueToStore: string = null;
		if (typeof value === 'string') {
			// Sanity check / defensive programming; if it's empty or just whitespace, something's wrong:
			if (value.trim().length === 0) {
				this.logger.warn(
					"Value for custom field list's '" +
						defForControl.view.label +
						"' dropdown " +
						'is an empty string and not a valid dropdown choice ID; value = ',
					value
				);
			} else {
				idValueToStore = value as string;
			}
		} else {
			this.logger.warn(
				"Value for custom field list's '" +
					defForControl.view.label +
					"' dropdown control is not of type, string, as expected; value = ",
				value
			);
		}
		return idValueToStore;
	}

	private handleNewNumberCustomFieldValue(defForControl: CustomFieldDefinition, value: any): number {
		let numberValueToStore: number = null;
		if (typeof value === 'number') {
			numberValueToStore = value as number;
		} else if (typeof value === 'string') {
			// Ok, we'll be helpful and MAYBE consider the empty string (or just whitespace) as OK, since the
			// Acceptance Criteria says it is.  We'd hope that would've been converted to null before it got
			// here, but...  No matter, be nice; complain to the log only if that's NOT the case:
			const valueAsString = (value as string).trim();
			if (valueAsString.length === 0) {
				// Fall-through; use null as the value
			} else {
				const maybeANumber = Number(valueAsString);
				if (Number.isNaN(maybeANumber)) {
					this.logger.warn(
						"Value for custom field number control, '" +
							defForControl.view.label +
							"', can't be converted to a number; value = ",
						value
					);
				} else {
					numberValueToStore = maybeANumber;
				}
			}
		} else {
			this.logger.warn(
				"Value for custom field list's control for '" +
					defForControl.view.label +
					"' is not of type, number, as expected",
				value
			);
		}
		return numberValueToStore;
	}

	private handleNewTextCustomFieldValue(defForControl: CustomFieldDefinition, value: any): string {
		let stringValueToStore: string;
		if (typeof value === 'string') {
			stringValueToStore = value as string;
			if (stringValueToStore === '') {
				stringValueToStore = null; // This removes it from Firebase if the user deleted the entire string
			}
		} else {
			this.logger.warn(
				"Value for custom field list's control for '" +
					defForControl.view.label +
					"' is not of type, string, as expected",
				value
			);
			stringValueToStore = null;
		}
		return stringValueToStore;
	}

	private handleNewLinkCustomFieldValue(defForControl: CustomFieldDefinition, value: any): string {
		let stringValueToStore: string;
		if (typeof value === 'string') {
			stringValueToStore = value as string;
			if (stringValueToStore === '') {
				stringValueToStore = null; // This removes it from Firebase if the user deleted the entire string
			}
		} else {
			this.logger.warn(
				"Value for custom field list's control for '" +
					defForControl.view.label +
					"' is not of type, string, as expected",
				value
			);
			stringValueToStore = null;
		}
		return stringValueToStore;
	}

	private listenForRequests() {
		this.saveCustomFieldsRequested$
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe(() => this.saveCustomFields());

		this.cancelCustomFieldsRequested$
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe(() => this.cancelCustomFields());
	}

	private saveCustomFields() {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7410
		// Mark all controls as dirty to trigger validation error display on submit.
		// detectChanges() ensures the UI updates after asynchronous saveCustomFieldsRequested$ emission.
		// Consider refactoring to handle validation and UI updates more robustly.
		if (this.customFieldsListFormGroup.invalid) {
			const controls = this.customFieldsListFormGroup.controls;
			for (const key of Object.keys(controls)) {
				this.customFieldsListFormGroup.controls[key].markAsDirty();
			}
			this.customFieldsListFormGroup.markAsDirty();
			this.cd.detectChanges();
			return;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7410
		const listType: CustomFieldListType = this.customFieldService.getTicketValueListType(
			this.isFloatingTickets
		);
		this.customFieldsApi.changeTicketValueView(
			this.editedTicketId,
			listType,
			DefaultSplitDataRenderType.database
		);
		this.customFieldsApi.persistTicketValue(this.editedTicketId, listType);
	}

	private cancelCustomFields(): void {
		const listType: CustomFieldListType = this.customFieldService.getTicketValueListType(
			this.isFloatingTickets
		);
		this.customFieldsApi.abortTicketValueView(this.editedTicketId, listType);
	}

	public ngOnDestroy() {
		this.shutdownSubject.next(true);
		this.editedTicketId = null;
		this.customFieldsApi = null;
		this.customFieldsListFormGroup = null;
		this.saveCustomFieldsRequested$ = null;
		this.cancelCustomFieldsRequested$ = null;
		this.orderedCustomFieldDef$ = null;
	}

	private logTicketData(data: EditTicketsCombined) {
		this.logger.log('ticketEditService.combinedEdit$ -->');
		this.logger.log('\t data.originalList.size = ' + data.originalList.size);
		this.logger.log('\t data.selectedType = ' + data.selectedType);
		this.logger.log('\t data.data {');
		let count = 0;
		for (const s of [
			'type',
			'constraintState',
			'actualPhase',
			'responsibleProjectMemberRoleId',
			'assignedProjectMemberRoleId',
		]) {
			if (data.data[s]) {
				this.logger.log('\t\t data.data.' + s + ' = ' + data.data[s]);
				count += 1;
			}
		}
		if (count === 0) {
			this.logger.log('\t\t ', data.data);
		}
		this.logger.log('\t }');
	}
}
